import React from "react";
import { styled } from "@mui/material/styles";
import PropTypes from "prop-types";

import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import SkuflixChevronRightIcon from "core/icons/skuflix/SkuflixChevronRightIcon";
import useCarousel from "core/hooks/useCarousel";
import { alpha } from "@mui/system";
import ProductCarouselItem from "arena/components/ProductCarouselItem";

const PREFIX = "ProductCarousel";

const classes = {
  scrollable: `${PREFIX}-scrollable`,
  grid: `${PREFIX}-grid`,
  item: `${PREFIX}-item`,
  prev: `${PREFIX}-prev`,
  next: `${PREFIX}-next`,
  btnRoot: `${PREFIX}-btnRoot`,
  icon: `${PREFIX}-icon`,
};

const Root = styled("div")(({ theme }) => ({
  position: "relative",
  overflow: "hidden",

  [`& .${classes.scrollable}`]: {
    overflowY: "hidden",
    overflowX: "auto",
    msOverflowStyle: "none", // IE 10+
    scrollbarWidth: "none", // Firefox
    "&::-webkit-scrollbar": {
      display: "none", // Safari and Chrome
    },
    scrollSnapType: "x mandatory",
  },

  [`& .${classes.grid}`]: {
    flexWrap: "nowrap",
  },

  [`& .${classes.item}`]: {
    flexShrink: 0,
    scrollSnapAlign: "start",
  },

  [`& .${classes.prev}`]: {
    position: "absolute",
    left: 0,
    top: 0,
    bottom: 0,
    zIndex: 1,
    width: 0,
    height: "100%",
    pointerEvents: "none",
    "& button": {
      left: 0,
      backgroundImage: `linear-gradient(90deg, ${
        theme.palette.background.body
      }, ${alpha(theme.palette.background.body, 0.6)}, transparent)`,
    },
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },

  [`& .${classes.next}`]: {
    position: "absolute",
    right: 0,
    top: 0,
    bottom: 0,
    zIndex: 1,
    width: 0,
    height: "100%",
    pointerEvents: "none",
    "& button": {
      right: 0,
      backgroundImage: `linear-gradient(-90deg, ${
        theme.palette.background.body
      }, ${alpha(theme.palette.background.body, 0.6)}, transparent)`,
    },
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },

  [`& .${classes.btnRoot}`]: {
    pointerEvents: "all",
    position: "absolute",
    top: 0,
    bottom: 0,
    width: 40,
    backgroundColor: "transparent",
    color: "rgba(255, 255, 255, 0.3)",
    "&:hover": {
      color: "#fff",
    },
    [theme.breakpoints.up("sm")]: {
      width: 60,
    },
    [theme.breakpoints.up("md")]: {
      width: 70,
    },
  },

  [`& .${classes.icon}`]: {
    fontSize: 40,
    [theme.breakpoints.up("sm")]: {
      fontSize: 50,
    },
  },
}));

export default function ProductCarousel({ nodes, xs, sm, md, lg, xl, title }) {
  const { rootRef, onScroll, prev, next, onClickPrev, onClickNext } =
    useCarousel(nodes.length);

  const onClickPromoVideoButton = () => {};

  if (!nodes || nodes.length === 0) {
    return null;
  }

  return (
    <>
      {title}

      <Box
        sx={{ pl: { xs: 2, sm: 0 }, pr: { xs: 2, sm: 0 } }}
        test-id="ProductCarousel"
      >
        <Root>
          <div className={classes.scrollable} ref={rootRef} onScroll={onScroll}>
            <Grid container spacing={3} className={classes.grid}>
              {nodes.map((product) => (
                <Grid
                  item
                  xs={xs}
                  sm={sm}
                  md={md}
                  lg={lg}
                  xl={xl}
                  key={product.id}
                  className={classes.item}
                >
                  <ProductCarouselItem
                    cardView="COMPACT"
                    product={product}
                    onClickPromoVideoButton={onClickPromoVideoButton}
                  />
                </Grid>
              ))}
            </Grid>
          </div>

          {prev && (
            <div className={classes.prev}>
              <ButtonBase
                classes={{ root: classes.btnRoot }}
                onClick={onClickPrev}
              >
                <SkuflixChevronRightIcon
                  className={classes.icon}
                  sx={{ transform: "scaleX(-1)" }}
                />
              </ButtonBase>
            </div>
          )}

          {next && (
            <div className={classes.next}>
              <ButtonBase
                classes={{ root: classes.btnRoot }}
                onClick={onClickNext}
              >
                <SkuflixChevronRightIcon className={classes.icon} />
              </ButtonBase>
            </div>
          )}
        </Root>
      </Box>
    </>
  );
}

ProductCarousel.defaultProps = {
  xs: 12,
  sm: 6,
  md: 4,
  lg: 4,
  xl: 4,
  nodes: [],
};

ProductCarousel.propTypes = {
  xs: PropTypes.oneOf([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]),
  sm: PropTypes.oneOf([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]),
  md: PropTypes.oneOf([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]),
  lg: PropTypes.oneOf([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]),
  xl: PropTypes.oneOf([1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]),
  collectionId: PropTypes.string,
  productId: PropTypes.string,
  nodes: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
    })
  ),
  count: PropTypes.number,
  title: PropTypes.node,
};
